import {
  validateRef, requireNonBlank, isPositiveInteger, isNonBlankString, isRecord,
  validateOrgExposureBindings,
  type DealThesisOrgExposureBinding,
} from './ma-deal-thesis-reference-validation';
export { thesisReferencesMatch } from './ma-deal-thesis-reference-validation';
export type { DealThesisOrgExposureBinding } from './ma-deal-thesis-reference-validation';
import type { DecisionAgentSourceClass } from './contracts/sourceClasses';

export const DEAL_THESIS_OUTCOME_SCHEMA_VERSION = 'deal_thesis_outcome_v1' as const;
export const DEAL_DISCONFIRMING_CONDITION_SCHEMA_VERSION =
  'deal_disconfirming_condition_v1' as const;

export const CLOSED_STRATEGY_RELATIONSHIP_KINDS = Object.freeze([
  'advances',
  'enables',
  'protects',
  'creates_option',
  'preserves_market_access',
] as const);

export const CLOSED_THESIS_CONDITION_CODES = Object.freeze([
  'critical_talent_loss',
  'named_customer_loss',
  'retention_below_threshold',
  'capability_not_retained',
  'product_or_channel_adoption_below_threshold',
  'regulatory_or_contractual_access_lost',
  'integration_dependency_missed',
  'standalone_business_deterioration',
] as const);

export const CLOSED_COMPARISON_OPERATORS = Object.freeze([
  'below',
  'below_or_equal',
  'equals',
  'above_or_equal',
  'above',
] as const);

export type ClosedStrategyRelationshipKind =
  (typeof CLOSED_STRATEGY_RELATIONSHIP_KINDS)[number];
export type ClosedThesisConditionCode = (typeof CLOSED_THESIS_CONDITION_CODES)[number];
export type ClosedComparisonOperator = (typeof CLOSED_COMPARISON_OPERATORS)[number];

export type GovernedRef<Kind extends string = string> = Readonly<{
  refKind: Kind;
  id: string;
  version?: number;
  effectiveAt?: string;
  tenantRef?: string;
  unitRef?: string;
  periodRef?: string;
}>;

export type DealRef = GovernedRef<'deal'>;
export type PromiseSetRef = GovernedRef<'promise_set'>;
export type DealThesisOutcomeRef = GovernedRef<'deal_thesis_outcome'>;
export type DealDisconfirmingConditionRef =
  GovernedRef<'deal_disconfirming_condition'>;
export type DealThesisInlineDisconfirmingConditionV1 = Readonly<{
  conditionRef: DealDisconfirmingConditionRef;
  statement: string;
  thresholdRef?: GovernedTargetRef;
}>;
export type StrategyObjectiveVersionRef = GovernedRef<'strategy_objective_version'>;
export type GovernedSourceRef = GovernedRef<'governed_source'>;
export type DealThesisSourceRef = GovernedSourceRef &
  Readonly<{ sourceClass?: DecisionAgentSourceClass }>;
export type GovernedSeatRef = GovernedRef<'governed_seat'>;
export type GovernedDateRef = GovernedRef<'governed_date'>;
export type PolicyRef = GovernedRef<'policy'>;
export type DealMeasurementBindingRef = GovernedRef<'deal_measurement_binding'>;
export type GovernedDependencyRef = GovernedRef<'governed_dependency'>;
export type DealCommitmentRef = GovernedRef<'deal_commitment'>;
export type GovernedEntityOrCapabilityRef =
  GovernedRef<'governed_entity_or_capability'>;
export type GovernedConstraintRef = GovernedRef<'governed_constraint'>;
export type GovernedTargetRef = GovernedRef<'governed_target'>;
export type GovernedPeriodRef = GovernedRef<'governed_period'>;

export type GovernedTextClaim = Readonly<{
  claim: string;
  sourceRefs: readonly DealThesisSourceRef[];
}>;

export type DealThesisOutcomeV1 = Readonly<{
  schemaVersion: typeof DEAL_THESIS_OUTCOME_SCHEMA_VERSION;
  thesisOutcomeRef: DealThesisOutcomeRef;
  dealRef: DealRef;
  promiseSetRef: PromiseSetRef;
  outcomeStatement: GovernedTextClaim;
  rationaleSourceRefs: readonly DealThesisSourceRef[];
  noDealCounterfactual?: GovernedTextClaim;
  strategyObjectiveRefAtApproval?: StrategyObjectiveVersionRef;
  currentStrategyObjectiveRef?: StrategyObjectiveVersionRef;
  relationshipKind: ClosedStrategyRelationshipKind;
  accountableSeatRef?: GovernedSeatRef;
  expectedOutcomeDateRef?: GovernedDateRef;
  reviewCadenceRef?: PolicyRef;
  successMeasurementBindingRefs: readonly DealMeasurementBindingRef[];
  leadingIndicatorBindingRefs: readonly DealMeasurementBindingRef[];
  laggingOutcomeBindingRefs: readonly DealMeasurementBindingRef[];
  dependencyRefs: readonly GovernedDependencyRef[];
  enablingCommitmentRefs: readonly DealCommitmentRef[];
  criticalAssetRefs: readonly GovernedEntityOrCapabilityRef[];
  guardrailRefs: readonly GovernedConstraintRef[];
  orgExposureBindings?: readonly DealThesisOrgExposureBinding[];
  disconfirmingConditionRefs?: readonly DealDisconfirmingConditionRef[];
  disconfirmingConditions?: readonly DealThesisInlineDisconfirmingConditionV1[];
}>;

export type DealDisconfirmingConditionV1 = Readonly<{
  schemaVersion: typeof DEAL_DISCONFIRMING_CONDITION_SCHEMA_VERSION;
  conditionRef: DealDisconfirmingConditionRef;
  thesisOutcomeRef: DealThesisOutcomeRef;
  conditionCode: ClosedThesisConditionCode;
  measurementBindingRef: DealMeasurementBindingRef;
  comparisonOperator: ClosedComparisonOperator;
  thresholdRef: GovernedTargetRef;
  evaluationWindowRef: GovernedPeriodRef;
  decisiveOrContributory: 'decisive' | 'contributory';
  policyRef: PolicyRef;
}>;

export type DealThesisOutcomeSetV1 = Readonly<{
  outcomes: readonly DealThesisOutcomeV1[];
  disconfirmingConditions: readonly DealDisconfirmingConditionV1[];
}>;

export type DealThesisOutcomeValidationResult =
  | Readonly<{ ok: true; value: DealThesisOutcomeV1 }>
  | Readonly<{ ok: false; errors: readonly string[] }>;

export type DealDisconfirmingConditionValidationResult =
  | Readonly<{ ok: true; value: DealDisconfirmingConditionV1 }>
  | Readonly<{ ok: false; errors: readonly string[] }>;

export type DealThesisOutcomeSetValidationResult =
  | Readonly<{ ok: true; value: DealThesisOutcomeSetV1 }>
  | Readonly<{ ok: false; errors: readonly string[] }>;

const DEAL_THESIS_OUTCOME_KEYS = Object.freeze([
  'schemaVersion',
  'thesisOutcomeRef',
  'dealRef',
  'promiseSetRef',
  'outcomeStatement',
  'rationaleSourceRefs',
  'noDealCounterfactual',
  'strategyObjectiveRefAtApproval',
  'currentStrategyObjectiveRef',
  'relationshipKind',
  'accountableSeatRef',
  'expectedOutcomeDateRef',
  'reviewCadenceRef',
  'successMeasurementBindingRefs',
  'leadingIndicatorBindingRefs',
  'laggingOutcomeBindingRefs',
  'dependencyRefs',
  'enablingCommitmentRefs',
  'criticalAssetRefs',
  'guardrailRefs',
  'orgExposureBindings',
  'disconfirmingConditionRefs',
  'disconfirmingConditions',
] as const);

const DEAL_THESIS_INLINE_DISCONFIRMING_CONDITION_KEYS = Object.freeze([
  'conditionRef',
  'statement',
  'thresholdRef',
] as const);

const DEAL_DISCONFIRMING_CONDITION_KEYS = Object.freeze([
  'schemaVersion',
  'conditionRef',
  'thesisOutcomeRef',
  'conditionCode',
  'measurementBindingRef',
  'comparisonOperator',
  'thresholdRef',
  'evaluationWindowRef',
  'decisiveOrContributory',
  'policyRef',
] as const);

const DEAL_THESIS_OUTCOME_SET_KEYS = Object.freeze([
  'outcomes',
  'disconfirmingConditions',
] as const);

export function validateDealThesisOutcomeV1(
  candidate: unknown,
): DealThesisOutcomeValidationResult {
  const errors: string[] = [];

  if (!isRecord(candidate)) {
    return { ok: false, errors: ['outcome must be an object'] };
  }

  rejectUnexpectedKeys(candidate, DEAL_THESIS_OUTCOME_KEYS, 'outcome', errors);
  validateOutcomeIdentity(candidate, errors);
  validateOutcomeReferenceArrays(candidate, errors);
  if (candidate.disconfirmingConditionRefs !== undefined) {
    validateRefArray(
      candidate.disconfirmingConditionRefs,
      'disconfirmingConditionRefs',
      'deal_disconfirming_condition',
      errors,
    );
  }
  if (candidate.disconfirmingConditions !== undefined) {
    validateInlineDisconfirmingConditions(
      candidate.disconfirmingConditions,
      'disconfirmingConditions',
      errors,
    );
  }
  validateDisconfirmingConditionRefReconciliation(candidate, errors);
  validateOrgExposureBindings(candidate, errors);

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  return { ok: true, value: candidate as DealThesisOutcomeV1 };
}

function validateOutcomeIdentity(candidate: Record<string, unknown>, errors: string[]): void {
  validateLiteral(
    candidate.schemaVersion,
    DEAL_THESIS_OUTCOME_SCHEMA_VERSION,
    'schemaVersion',
    errors,
  );
  validateRef(candidate.thesisOutcomeRef, 'thesisOutcomeRef', 'deal_thesis_outcome', errors);
  validateRef(candidate.dealRef, 'dealRef', 'deal', errors);
  validateRef(candidate.promiseSetRef, 'promiseSetRef', 'promise_set', errors);
  validateTextClaim(candidate.outcomeStatement, 'outcomeStatement', errors);
  validateRefArray(
    candidate.rationaleSourceRefs,
    'rationaleSourceRefs',
    'governed_source',
    errors,
    { requireNonEmpty: true },
  );
  if (candidate.noDealCounterfactual !== undefined) {
    validateTextClaim(candidate.noDealCounterfactual, 'noDealCounterfactual', errors);
  }
  if (candidate.strategyObjectiveRefAtApproval !== undefined) {
    validateStrategyObjectiveVersionRef(
      candidate.strategyObjectiveRefAtApproval,
      'strategyObjectiveRefAtApproval',
      errors,
    );
  }
  if (candidate.currentStrategyObjectiveRef !== undefined) {
    validateStrategyObjectiveVersionRef(
      candidate.currentStrategyObjectiveRef,
      'currentStrategyObjectiveRef',
      errors,
    );
  }
  validateEnum(
    candidate.relationshipKind,
    CLOSED_STRATEGY_RELATIONSHIP_KINDS,
    'relationshipKind',
    'relationshipKind must be one of the closed strategy relationship kinds',
    errors,
  );
  for (const [key, refKind] of [
    ['accountableSeatRef', 'governed_seat'],
    ['expectedOutcomeDateRef', 'governed_date'],
    ['reviewCadenceRef', 'policy'],
  ]) {
    if (candidate[key] !== undefined) validateRef(candidate[key], key, refKind, errors);
  }
}

function validateOutcomeReferenceArrays(candidate: Record<string, unknown>, errors: string[]): void {
  validateRefArray(
    candidate.successMeasurementBindingRefs,
    'successMeasurementBindingRefs',
    'deal_measurement_binding',
    errors,
  );
  validateRefArray(
    candidate.leadingIndicatorBindingRefs,
    'leadingIndicatorBindingRefs',
    'deal_measurement_binding',
    errors,
  );
  validateRefArray(
    candidate.laggingOutcomeBindingRefs,
    'laggingOutcomeBindingRefs',
    'deal_measurement_binding',
    errors,
  );
  validateRefArray(candidate.dependencyRefs, 'dependencyRefs', 'governed_dependency', errors);
  validateRefArray(
    candidate.enablingCommitmentRefs,
    'enablingCommitmentRefs',
    'deal_commitment',
    errors,
  );
  validateRefArray(
    candidate.criticalAssetRefs,
    'criticalAssetRefs',
    'governed_entity_or_capability',
    errors,
  );
  validateRefArray(candidate.guardrailRefs, 'guardrailRefs', 'governed_constraint', errors);
}

function validateInlineDisconfirmingConditions(
  value: unknown,
  path: string,
  errors: string[],
): void {
  if (!Array.isArray(value)) {
    errors.push(`${path} must be an array`);
    return;
  }

  const seen = new Set<string>();
  for (const [index, condition] of value.entries()) {
    const conditionPath = `${path}[${index}]`;
    if (!isRecord(condition)) {
      errors.push(`${conditionPath} must be an object`);
      continue;
    }
    rejectUnexpectedKeys(
      condition,
      DEAL_THESIS_INLINE_DISCONFIRMING_CONDITION_KEYS,
      conditionPath,
      errors,
    );
    validateRef(
      condition.conditionRef,
      `${conditionPath}.conditionRef`,
      'deal_disconfirming_condition',
      errors,
    );
    requireNonBlank(condition.statement, `${conditionPath}.statement`, errors);
    if (condition.thresholdRef !== undefined) {
      validateRef(
        condition.thresholdRef,
        `${conditionPath}.thresholdRef`,
        'governed_target',
        errors,
      );
    }

    const conditionId = readRefId(condition, 'conditionRef');
    if (!conditionId) continue;
    if (seen.has(conditionId)) {
      errors.push(`${path} contains duplicate conditionRef id: ${conditionId}`);
    }
    seen.add(conditionId);
  }
}

function validateDisconfirmingConditionRefReconciliation(
  candidate: Record<string, unknown>,
  errors: string[],
): void {
  const conditionRefIds = readRefIds(candidate, 'disconfirmingConditionRefs');
  if (conditionRefIds.length === 0) {
    return;
  }

  const inlineConditions = candidate.disconfirmingConditions;
  if (!Array.isArray(inlineConditions) || inlineConditions.length === 0) {
    errors.push(
      'disconfirmingConditionRefs must match inline disconfirmingConditions when supplied',
    );
    return;
  }

  const inlineConditionIds = inlineConditions
    .map((condition) => readRefId(condition, 'conditionRef'))
    .filter((conditionId): conditionId is string => conditionId !== null);
  const inlineConditionIdSet = new Set(inlineConditionIds);
  const conditionRefIdSet = new Set(conditionRefIds);

  for (const conditionRefId of conditionRefIdSet) {
    if (!inlineConditionIdSet.has(conditionRefId)) {
      errors.push(
        `disconfirmingConditionRefs contains conditionRef not declared inline: ${conditionRefId}`,
      );
    }
  }

  for (const inlineConditionId of inlineConditionIdSet) {
    if (!conditionRefIdSet.has(inlineConditionId)) {
      errors.push(
        `disconfirmingConditions contains conditionRef not declared by disconfirmingConditionRefs: ${inlineConditionId}`,
      );
    }
  }
}

export function validateDealDisconfirmingConditionV1(
  candidate: unknown,
): DealDisconfirmingConditionValidationResult {
  const errors: string[] = [];

  if (!isRecord(candidate)) {
    return { ok: false, errors: ['condition must be an object'] };
  }

  rejectUnexpectedKeys(candidate, DEAL_DISCONFIRMING_CONDITION_KEYS, 'condition', errors);
  validateLiteral(
    candidate.schemaVersion,
    DEAL_DISCONFIRMING_CONDITION_SCHEMA_VERSION,
    'schemaVersion',
    errors,
  );
  validateRef(candidate.conditionRef, 'conditionRef', 'deal_disconfirming_condition', errors);
  validateRef(candidate.thesisOutcomeRef, 'thesisOutcomeRef', 'deal_thesis_outcome', errors);
  validateEnum(
    candidate.conditionCode,
    CLOSED_THESIS_CONDITION_CODES,
    'conditionCode',
    'conditionCode must be one of the closed thesis condition codes',
    errors,
  );
  validateRef(
    candidate.measurementBindingRef,
    'measurementBindingRef',
    'deal_measurement_binding',
    errors,
  );
  validateEnum(
    candidate.comparisonOperator,
    CLOSED_COMPARISON_OPERATORS,
    'comparisonOperator',
    'comparisonOperator must be one of the closed comparison operators',
    errors,
  );
  validateRef(candidate.thresholdRef, 'thresholdRef', 'governed_target', errors);
  validateRef(candidate.evaluationWindowRef, 'evaluationWindowRef', 'governed_period', errors);
  validateEnum(
    candidate.decisiveOrContributory,
    ['decisive', 'contributory'],
    'decisiveOrContributory',
    'decisiveOrContributory must be decisive or contributory',
    errors,
  );
  validateRef(candidate.policyRef, 'policyRef', 'policy', errors);
  validateThresholdCompatibility(candidate, errors);

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  return { ok: true, value: candidate as DealDisconfirmingConditionV1 };
}

export function validateDealThesisOutcomeSetV1(
  candidate: unknown,
): DealThesisOutcomeSetValidationResult {
  const errors: string[] = [];

  if (!isRecord(candidate)) {
    return { ok: false, errors: ['outcome set must be an object'] };
  }

  rejectUnexpectedKeys(candidate, DEAL_THESIS_OUTCOME_SET_KEYS, 'outcomeSet', errors);

  const outcomes = Array.isArray(candidate.outcomes) ? candidate.outcomes : [];
  const disconfirmingConditions = Array.isArray(candidate.disconfirmingConditions)
    ? candidate.disconfirmingConditions
    : [];

  if (!Array.isArray(candidate.outcomes)) {
    errors.push('outcomes must be an array');
  }
  if (!Array.isArray(candidate.disconfirmingConditions)) {
    errors.push('disconfirmingConditions must be an array');
  }

  for (const [index, outcome] of outcomes.entries()) {
    const result = validateDealThesisOutcomeV1(outcome);
    if (!result.ok) {
      errors.push(...result.errors.map((error) => `outcomes[${index}].${error}`));
    }
  }

  for (const [index, condition] of disconfirmingConditions.entries()) {
    const result = validateDealDisconfirmingConditionV1(condition);
    if (!result.ok) {
      errors.push(...result.errors.map((error) => `disconfirmingConditions[${index}].${error}`));
    }
  }

  rejectDuplicateRefs(
    outcomes,
    (outcome) => readRefId(outcome, 'thesisOutcomeRef'),
    'duplicate thesisOutcomeRef id',
    errors,
  );
  rejectDuplicateRefs(
    disconfirmingConditions,
    (condition) => readRefId(condition, 'conditionRef'),
    'duplicate conditionRef id',
    errors,
  );
  validateDisconfirmingConditionGraphEdges(
    outcomes,
    disconfirmingConditions,
    errors,
  );

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  return { ok: true, value: candidate as DealThesisOutcomeSetV1 };
}

function validateStrategyObjectiveVersionRef(
  value: unknown,
  path: string,
  errors: string[],
): void {
  validateRef(value, path, 'strategy_objective_version', errors);
  if (!isRecord(value)) {
    return;
  }

  if (!isPositiveInteger(value.version)) {
    errors.push(`${path}.version is required`);
  }
  if (!isNonBlankString(value.effectiveAt)) {
    errors.push(`${path}.effectiveAt is required`);
  }
}

function validateDisconfirmingConditionGraphEdges(
  outcomes: readonly unknown[],
  disconfirmingConditions: readonly unknown[],
  errors: string[],
): void {
  const outcomeRefIds = new Set<string>();
  const listedConditionIdsByOutcomeId = new Map<string, Set<string>>();

  for (const outcome of outcomes) {
    const thesisOutcomeId = readRefId(outcome, 'thesisOutcomeRef');
    if (!thesisOutcomeId) {
      continue;
    }

    outcomeRefIds.add(thesisOutcomeId);
    listedConditionIdsByOutcomeId.set(
      thesisOutcomeId,
      new Set(readRefIds(outcome, 'disconfirmingConditionRefs')),
    );
  }

  const conditionEdgesById = new Map<
    string,
    Readonly<{ index: number; thesisOutcomeId: string | null }>
  >();

  for (const [index, condition] of disconfirmingConditions.entries()) {
    const conditionId = readRefId(condition, 'conditionRef');
    const thesisOutcomeId = readRefId(condition, 'thesisOutcomeRef');

    if (thesisOutcomeId && !outcomeRefIds.has(thesisOutcomeId)) {
      errors.push(
        `disconfirmingConditions[${index}].thesisOutcomeRef id is not declared by outcomes: ${thesisOutcomeId}`,
      );
    }

    if (!conditionId) {
      continue;
    }

    conditionEdgesById.set(conditionId, { index, thesisOutcomeId });

    const listedConditionIds = thesisOutcomeId
      ? listedConditionIdsByOutcomeId.get(thesisOutcomeId)
      : undefined;
    if (
      thesisOutcomeId &&
      outcomeRefIds.has(thesisOutcomeId) &&
      !listedConditionIds?.has(conditionId)
    ) {
      errors.push(
        `disconfirmingConditions[${index}].conditionRef id is not listed by its thesis outcome ${thesisOutcomeId}: ${conditionId}`,
      );
    }
  }

  for (const [outcomeIndex, outcome] of outcomes.entries()) {
    const thesisOutcomeId = readRefId(outcome, 'thesisOutcomeRef');
    if (!thesisOutcomeId) {
      continue;
    }

    for (const conditionId of readRefIds(outcome, 'disconfirmingConditionRefs')) {
      const conditionEdge = conditionEdgesById.get(conditionId);
      if (!conditionEdge) {
        errors.push(
          `outcomes[${outcomeIndex}].disconfirmingConditionRefs contains missing conditionRef id: ${conditionId}`,
        );
        continue;
      }

      if (
        conditionEdge.thesisOutcomeId &&
        conditionEdge.thesisOutcomeId !== thesisOutcomeId
      ) {
        errors.push(
          `condition ${conditionId} is listed by ${thesisOutcomeId} but points to ${conditionEdge.thesisOutcomeId}`,
        );
      }
    }
  }
}

function validateThresholdCompatibility(
  candidate: Record<string, unknown>,
  errors: string[],
): void {
  if (!isRecord(candidate.measurementBindingRef) || !isRecord(candidate.thresholdRef)) {
    return;
  }

  if (typeof candidate.measurementBindingRef.unitRef !== 'string') {
    errors.push('measurementBindingRef.unitRef is required');
  }
  if (typeof candidate.thresholdRef.unitRef !== 'string') {
    errors.push('thresholdRef.unitRef is required');
  }
  if (
    typeof candidate.measurementBindingRef.unitRef === 'string' &&
    typeof candidate.thresholdRef.unitRef === 'string' &&
    candidate.measurementBindingRef.unitRef !== candidate.thresholdRef.unitRef
  ) {
    errors.push('thresholdRef.unitRef must match measurementBindingRef.unitRef');
  }

  if (typeof candidate.measurementBindingRef.periodRef !== 'string') {
    errors.push('measurementBindingRef.periodRef is required');
  }
  if (typeof candidate.thresholdRef.periodRef !== 'string') {
    errors.push('thresholdRef.periodRef is required');
  }
  if (
    typeof candidate.measurementBindingRef.periodRef === 'string' &&
    typeof candidate.thresholdRef.periodRef === 'string' &&
    candidate.measurementBindingRef.periodRef !== candidate.thresholdRef.periodRef
  ) {
    errors.push('thresholdRef.periodRef must match measurementBindingRef.periodRef');
  }
}

function validateTextClaim(
  value: unknown,
  path: string,
  errors: string[],
): void {
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`);
    return;
  }

  requireNonBlank(value.claim, `${path}.claim`, errors);
  validateRefArray(value.sourceRefs, `${path}.sourceRefs`, 'governed_source', errors, {
    requireNonEmpty: true,
  });
}

function validateRefArray(
  value: unknown,
  path: string,
  refKind: string,
  errors: string[],
  options: Readonly<{ requireNonEmpty?: boolean; nonEmptyMessage?: string }> = {},
): void {
  if (!Array.isArray(value)) {
    errors.push(`${path} must be an array`);
    return;
  }

  if (options.requireNonEmpty && value.length === 0) {
    errors.push(options.nonEmptyMessage ?? `${path} must not be empty`);
  }

  const seen = new Set<string>();
  for (const [index, item] of value.entries()) {
    validateRef(item, `${path}[${index}]`, refKind, errors);
    const id = isRecord(item) && typeof item.id === 'string' ? item.id : null;
    if (id && seen.has(id)) {
      errors.push(`${path} contains duplicate ref id: ${id}`);
    }
    if (id) {
      seen.add(id);
    }
  }
}

function validateLiteral(
  value: unknown,
  expected: string,
  path: string,
  errors: string[],
): void {
  if (value !== expected) {
    errors.push(`${path} must be ${expected}`);
  }
}

function validateEnum<T extends string>(
  value: unknown,
  allowed: readonly T[],
  _path: string,
  message: string,
  errors: string[],
): void {
  if (typeof value !== 'string' || !allowed.includes(value as T)) {
    errors.push(message);
  }
}

function rejectUnexpectedKeys(
  value: Record<string, unknown>,
  allowedKeys: readonly string[],
  path: string,
  errors: string[],
): void {
  for (const key of Object.keys(value)) {
    if (!allowedKeys.includes(key)) {
      errors.push(`${path}.${key} is not allowed`);
    }
  }
}

function rejectDuplicateRefs<T>(
  values: readonly T[],
  readId: (value: T) => string | null,
  messagePrefix: string,
  errors: string[],
): void {
  const seen = new Set<string>();
  for (const value of values) {
    const id = readId(value);
    if (!id) {
      continue;
    }
    if (seen.has(id)) {
      errors.push(`${messagePrefix}: ${id}`);
    }
    seen.add(id);
  }
}

function readRefId(value: unknown, refField: string): string | null {
  if (!isRecord(value)) {
    return null;
  }
  const refValue = value[refField];
  if (!isRecord(refValue) || typeof refValue.id !== 'string') {
    return null;
  }
  return refValue.id;
}

function readRefIds(value: unknown, refField: string): readonly string[] {
  if (!isRecord(value)) {
    return [];
  }

  const refValues = value[refField];
  if (!Array.isArray(refValues)) {
    return [];
  }

  return refValues
    .map((refValue) =>
      isRecord(refValue) && typeof refValue.id === 'string' ? refValue.id : null,
    )
    .filter((id): id is string => id !== null);
}
